<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    tabindex="-1"
    v-if="isVisible"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditing ? "Editar" : "Crear" }} {{ modelLabel }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <ContentSkeleton v-if="!isFormReady" variant="form" :rows="6" />
          <div v-else class="referencemodal">
            <AutoSaveForm
              v-if="visualGroupsLoaded"
              ref="autoSaveForm"
              :formData="formData"
              :manual-save-in-progress="isSubmitting"
              :modelName="modelName"
              :processFormData="processFormData"
              :modelFields="modelFields"
              :parentID="parentID"
              :parentModel="selectedModel"
              :disabled="['shipping', 'payment'].includes((modelName || '').toLowerCase())"
              @unsaved-changes="onUnsavedChanges"
              @auto-save-success="onAutoSaveSuccess"
              @auto-save-error="onAutoSaveError"
            >
              <Form
                :fields="modelFields"
                v-model="formData"
                :lists="lists"
                :visualGroups="localVisualGroups"
                :modelDefinitions="modelDefinitions"
                :models="models"
                :selectedModel="modelName"
                :defaultFormData="formData"
                :parentID="parentID"
                @button-click="onButtonClick"
              />
            </AutoSaveForm>
            <ContentSkeleton v-else variant="form" :rows="6" />
            <div class="mt-3">
              <button
                type="button"
                class="btn btn-primary me-2"
                :disabled="isSubmitting"
                @click="onSubmit(formData)"
              >
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Guardar
              </button>
              <button type="button" class="btn btn-secondary" @click="close">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { promptFindTeuTrackingOnSave } from "@/utils/findTeuPromptTrackingOnSave";
import Swal from "sweetalert2";
import {
  getApiErrorData,
  isHookValidationError,
  promptHookValidationForceSave,
  showForceSaveRetryError,
} from "@/utils/dynamicModelValidationSwal";
import Form from "./Form2.vue";
import AutoSaveForm from "./AutoSaveForm.vue";
import { newClientCreateKey } from "@/utils/clientCreateKey";
import {
  processReferenceFormData,
  removeReferenceGroupIds,
} from "./referenceModalFormProcessing.js";
import { copyTranslatableSiblingsInto } from "@/utils/fieldValueI18n";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "ReferenceModal",
  components: {
    ContentSkeleton,
    Form,
    AutoSaveForm,
  },
  props: {
    extraParams: {
      type: Object,
      default: () => {},
    },
    selectedModel: {
      type: String,
      default: null,
    },
    parentID: {
      type: String,
      default: null,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelLabel: {
      type: String,
      required: true,
    },
    lists: {
      type: Array,
      default: () => [],
    },
    itemId: {
      type: String,
      default: null,
    },
    editingItem: {
      type: Object,
      default: null,
    },
    visualGroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      models: [],
      formData: {},
      isEditing: false,
      isFormReady: false,
      initialized: false,
      referenceOptions: {},
      visualGroupsLoaded: false,
      modelFields: [],
      modelDefinitions: {},
      localVisualGroups: [], // Local copy of visualGroups prop
      isSubmitting: false,
      // Clave de alta de este formulario (ver utils/clientCreateKey): aunque el autoguardado y
      // «Guardar» pidan el alta a la vez, el backend crea un solo documento.
      clientCreateKey: newClientCreateKey(),
    };
  },

  async mounted() {
    try {
      await this.fetchModels();
    } catch (error) {
      console.error("Error in mounted:", error);
    } finally {
      this.isLoading = false;
    }
  },
  created() {
    this.initializeFormData();
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.initializeFormData();
            this.loadReferenceOptions();
            this.loadModelDefinition();
          });
        } else {
          // Reset form when modal is closed
          this.formData = {};
          this.isFormReady = false;
          this.initialized = false;
          this.localVisualGroups = [];
          this.visualGroupsLoaded = false;
        }
      },
    },
    modelFields: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.initializeFormData();
          });
        }
      },
    },
    editingItem: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.isEditing = true;
          // Modelos sin visualGroups (p. ej. shipping_expenses) igual tienen fields cargados
          if (this.visualGroupsLoaded && this.modelFields && this.modelFields.length > 0) {
            this.processEditingItem(newVal);
          }
        } else {
          this.isEditing = false;
        }
      },
    },
    visualGroups: {
      immediate: true,
      handler(newVal) {
        // Sync prop with local data
        this.localVisualGroups = [...(newVal || [])];

        if (newVal && newVal.length > 0 && this.editingItem) {
          this.processEditingItem(this.editingItem);
        }
      },
    },
  },
  methods: {
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
      // Cargar todas las definiciones de modelos
      const modelDefinitions = {};
      for (const model of data) {
        modelDefinitions[model.modelName] = model;
      }
      this.modelDefinitions = modelDefinitions;
    },
    async loadReferenceOptions() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.referenceOptions = data;
      } catch (error) {
        console.error("Error loading reference options:", error);
      }
    },
    async loadModelDefinition() {
      try {
        // Convertir extraParams en parámetros individuales de la URL
        const extraParamsString =
          this.extraParams && typeof this.extraParams === "object"
            ? Object.entries(this.extraParams)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join("&")
            : "";

        const url = `/api/gotrading/model-definitions/${
          this.modelName
        }?parentID=${this.parentID}${
          extraParamsString ? "&" + extraParamsString : ""
        }&parentModel=${this.selectedModel}`;
        const { data } = await wencesApi.get(url);

        // Mostrar mensajes de operación si existen antes de redirigir
        if (data && data._operationMessages && Array.isArray(data._operationMessages)) {
          for (const msg of data._operationMessages) {
            const isError = msg.type === 'error';
            await Swal.fire({
              icon: isError ? 'error' : msg.type === 'success' ? 'success' : 'info',
              title: isError ? 'Error' : msg.type === 'success' ? 'Éxito' : 'Información',
              text: msg.message,
              confirmButtonText: 'Entendido',
              confirmButtonColor: isError ? '#dc3545' : msg.type === 'success' ? '#28a745' : '#17a2b8'
            });
          }
        }

        if(data.redirect){
          this.$router.push(`/${data.redirect}`);
          return;
        }

        // Modelos grupo (p. ej. shipping_expenses) devuelven `fields` sin `visualGroups`;
        // antes solo se asignaba modelFields si había visualGroups → spinner infinito al editar.
        if (data && Array.isArray(data.fields)) {
          const isPaymentCreate =
            String(this.modelName || "").toLowerCase() === "payment" &&
            !this.editingItem &&
            !this.itemId;
          if (isPaymentCreate) {
            const hideOnCreate = new Set([
              "estimated_paymentdate",
              "responsable",
            ]);
            this.modelFields = data.fields.map((field) =>
              field && hideOnCreate.has(field.key)
                ? { ...field, hide: true }
                : field
            );
          } else {
            this.modelFields = data.fields;
          }
        }
        if (data && Array.isArray(data.visualGroups)) {
          this.localVisualGroups = data.visualGroups;
        } else if (data && !data.visualGroups) {
          this.localVisualGroups = [];
        }
        this.visualGroupsLoaded = true;

        // Si hay un editingItem esperando, procesarlo cuando ya hay campos (con o sin grupos visuales)
        if (this.editingItem && this.modelFields && this.modelFields.length > 0) {
          this.processEditingItem(this.editingItem);
        }
      } catch (error) {
        console.error("Error loading model definition:", error);
        this.visualGroupsLoaded = true; // También marcamos como cargado en caso de error
      }
    },
    initializeFormData() {
      if (!this.modelFields || this.modelFields.length === 0) {
        return;
      }

      // Si estamos en modo edición y tenemos un editingItem, no inicializamos con valores por defecto
      if (this.isEditing && this.editingItem) {
        this.isFormReady = true;
        this.initialized = true;
        return;
      }

      this.isFormReady = false;
      this.initialized = false;

      const newFormData = {};

      this.modelFields.forEach((field) => {
        let defaultValue;

        switch (field.type) {
          case "array":
          case "group":
            defaultValue = field.isArray ? [] : {};
            break;
          case "boolean":
            defaultValue = false;
            break;
          case "number":
            defaultValue = 0;
            break;
          case "date":
            defaultValue = null;
            break;
          case "reference":
          case "list":
            defaultValue = field.isArray ? [] : null;
            break;
          default:
            defaultValue = "";
        }

        newFormData[field.key] = defaultValue;
      });

      this.$nextTick(() => {
        this.formData = { ...newFormData };
        this.initialized = true;
        this.isFormReady = true;
      });
    },
    processEditingItem(item) {
      const formData = { ...item };

      // Procesar visual groups
      this.localVisualGroups.forEach((group) => {
        const groupKey = `group_${group.name}`;
        formData[groupKey] = {};

        this.modelFields.forEach((field) => {
          if (
            field.visualGroup === group.name &&
            formData[field.key] !== undefined
          ) {
            if (field.type === "date" && formData[field.key] && !field.time) {
              const date = new Date(formData[field.key]);
              formData[groupKey][field.key] = date.toISOString().split("T")[0];
            } else if (
              field.type === "date" &&
              formData[field.key] &&
              field.time
            ) {
              // NO transformar campos datetime que ya están en formato correcto
              // La transformación de zona horaria ya se hace en el backend
              if (
                typeof formData[field.key] === "string" &&
                formData[field.key].includes("T")
              ) {
                // Si ya está en formato datetime-local (sin Z, +, -), dejarlo tal como está
                if (
                  !formData[field.key].includes("Z") &&
                  !formData[field.key].includes("+") &&
                  !formData[field.key].includes("-")
                ) {
                  // Solo asegurar que esté en formato YYYY-MM-DDTHH:mm
                  formData[groupKey][field.key] = formData[field.key].slice(
                    0,
                    16
                  );
                } else {
                  // Si tiene zona horaria (Z, +, -), NO transformar aquí
                  // La transformación se hace en el backend
                  formData[groupKey][field.key] = formData[field.key];
                }
              } else {
                // Solo transformar si no es un string con formato datetime
                const date = new Date(formData[field.key]);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                formData[groupKey][
                  field.key
                ] = `${year}-${month}-${day}T${hours}:${minutes}`;
              }
            } else {
              formData[groupKey][field.key] = formData[field.key];
            }
            formData[field.key] = formData[groupKey][field.key];
            copyTranslatableSiblingsInto(formData[groupKey], formData, field);
          }
        });
      });

      this.formData = formData;
      this.isFormReady = true;
      this.initialized = true;
    },
    /** Modo «Preguntar» del mapeo FindTeu: ofrece activar el seguimiento tras guardar. */
    async promptFindTeuTracking(itemId) {
      await promptFindTeuTrackingOnSave({
        modelName: this.modelName,
        itemId,
        t: (key, params) => this.$t(key, params),
      });
    },
    async onSubmit(formData) {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        // Si el autoguardado está dando de alta este registro, esperarlo: si no, este guardado
        // sale sin `_id` y pide otra alta (dos documentos y dos correlativos).
        const autoSaveForm = this.$refs.autoSaveForm;
        if (autoSaveForm && typeof autoSaveForm.waitForPendingSave === "function") {
          await autoSaveForm.waitForPendingSave();
        }

        // Crear una copia del formulario para no modificar el original
        const dataToSend = await this.processFormData(formData);

        if (!dataToSend._id) {
          delete dataToSend.idModel;
          delete dataToSend.undefined;
          delete dataToSend.createdBy;
          delete dataToSend.updatedBy;
        }

        // Si estamos creando (no editando), eliminar recursivamente los _id de los grupos
        if (!dataToSend._id) {
          this.removeGroupIds(dataToSend, this.modelFields);
        }

        dataToSend.parentID = this.parentID;
        dataToSend.parentModel = this.selectedModel;
console.log("dataToSend", dataToSend);
        let response;
        // `dataToSend._id` y no `editingItem._id`: en un alta el id puede venir del autoguardado.
        if (dataToSend._id && String(dataToSend._id).length > 0) {
          response = await wencesApi.put(
            `/api/gotrading/dynamic/${this.modelName}/${dataToSend._id}`,
            dataToSend
          );
        } else {
          response = await wencesApi.post(`/api/gotrading/dynamic/${this.modelName}`, {
            ...dataToSend,
            _clientCreateKey: this.clientCreateKey,
          });
        }

        await Swal.fire({
          title: "¡Éxito!",
          text: `Registro ${
            this.isEditing ? "actualizado" : "creado"
          } correctamente`,
          icon: "success",
        });

        await this.promptFindTeuTracking(response.data?._id || dataToSend._id);
        this.$emit("saved", response.data);
        this.close();
      } catch (error) {
        console.error("Error saving reference:", error);

        const errorData = getApiErrorData(error);
        if (errorData && isHookValidationError(errorData)) {
          const confirmed = await promptHookValidationForceSave(
            (key, params) => this.$t(key, params),
            errorData.message,
            { modelName: this.modelName }
          );

          if (confirmed) {
            try {
              const dataToSend = await this.processFormData(this.formData);
              if (!dataToSend._id) {
                delete dataToSend.idModel;
                delete dataToSend.undefined;
                delete dataToSend.createdBy;
                delete dataToSend.updatedBy;
                this.removeGroupIds(dataToSend, this.modelFields);
              }
              dataToSend.parentID = this.parentID;
              dataToSend.parentModel = this.selectedModel;

              let response;
              if (dataToSend._id && String(dataToSend._id).length > 0) {
                response = await wencesApi.put(
                  `/api/gotrading/dynamic/${this.modelName}/${dataToSend._id}`,
                  { ...dataToSend, forceSave: true }
                );
              } else {
                response = await wencesApi.post(`/api/gotrading/dynamic/${this.modelName}`, {
                  ...dataToSend,
                  forceSave: true,
                  _clientCreateKey: this.clientCreateKey,
                });
              }

              await Swal.fire({
                title: "¡Éxito!",
                text: `Registro ${
                  this.isEditing ? "actualizado" : "creado"
                } correctamente`,
                icon: "success",
              });

              await this.promptFindTeuTracking(response.data?._id || dataToSend._id);
              this.$emit("saved", response.data);
              this.close();
            } catch (retryErr) {
              console.error("Error forceSave reference:", retryErr);
              await showForceSaveRetryError(
                (key, params) => this.$t(key, params),
                retryErr
              );
            }
          }
        } else {
          let errorMessage = "No se pudo guardar el registro";
          if (errorData) {
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          await Swal.fire({
            title: "Error",
            text: errorMessage,
            icon: "error",
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async onButtonClick(val) {
      const dataToSave = await this.processFormData(this.formData);
      await wencesApi
        .post(`/api/gotrading/dynamic/${this.modelName}/preload`, {
          action: val.value,
          item: dataToSave,
        })
        .then((response) => {
          this.formData = response.data.data;
          this.fetchItemPreload();
        });
    },
    async fetchItemPreload() {
      // Ya no necesitamos transformar fechas aquí porque vienen transformadas del backend

      const formData = { ...this.formData };

      // Procesar visual groups si existen
      if (this.localVisualGroups && this.localVisualGroups.length > 0) {
        this.localVisualGroups.forEach((group) => {
          const groupKey = `group_${group.name}`;
          formData[groupKey] = {};

          this.modelFields.forEach((field) => {
            if (
              field.visualGroup === group.name &&
              formData[field.key] !== undefined
            ) {
              // Las fechas ya vienen transformadas del backend, solo copiarlas
              formData[groupKey][field.key] = formData[field.key];
              copyTranslatableSiblingsInto(formData[groupKey], formData, field);
            }
          });
        });
      }

      this.formData = formData;
    },
    async processFormData(form) {
      return processReferenceFormData(
        form,
        this.modelFields,
        this.localVisualGroups
      );
    },
    removeGroupIds(data, fields) {
      removeReferenceGroupIds(data, fields);
    },
    close() {
      this.formData = {};
      this.isEditing = false;
      this.isFormReady = false;
      this.initialized = false;
      this.$emit("close");
    },

    /**
     * Manejar cambios sin guardar
     */
    onUnsavedChanges(hasChanges) {
      // Opcional: manejar cambios sin guardar
    },

    /**
     * Manejar éxito del auto-guardado
     */
    onAutoSaveSuccess(responseData) {
      // Opcional: mostrar notificación de éxito
    },

    /**
     * Manejar error del auto-guardado
     */
    onAutoSaveError(error) {
      console.error("❌ [REFERENCE-MODAL] Error en auto-guardado:", error);
      // Opcional: mostrar notificación de error
    },

    /**
     * Manejar actualización de ID (para nuevos registros)
     */
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
  /* El modal ya era el contenedor de scroll (vertical); habilitarle también el
     horizontal deja alcanzar los grupos múltiples anchos sin crear un recorte
     nuevo que corte los desplegables de los multiselect. */
  overflow-x: auto;
}

/* Estos formularios suelen traer grupos múltiples de muchas columnas: el xl de
   Bootstrap (1140px) se queda corto en pantallas grandes. */
.modal-dialog {
  max-width: min(1600px, 96%);
}

/* El sidebar es fijo y queda por encima del modal, tapándole el lado izquierdo:
   corremos el área del modal en vez de solaparla. 260px expandido, 80px cuando
   el usuario lo colapsa (`body.sidebar-mini`); abajo de lg el sidebar no ocupa
   lugar propio, así que el modal usa todo el ancho. */
@media (min-width: 992px) {
  .modal {
    left: 260px;
    width: calc(100% - 260px);
  }

  body.sidebar-mini .modal {
    left: 80px;
    width: calc(100% - 80px);
  }
}

.modal.show {
  display: block;
}
</style>
